import React, { useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../redux/hooks'
import { toggleSidebar, closeMobileSidebar } from '../../redux/reducers/settingsSlice'
import { CONFIG } from '../../../config'
import SalextLogo from '../../assets/salext-logo.svg'
import SalextLogoWhite from '../../assets/salext-logo-white.svg'
import {
  LayoutDashboard,
  Users,
  Activity,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  UserCog,
  Building2,
  Mail,
  Printer,
  X,
  Trash2,
} from 'lucide-react'

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { sidebarCollapsed, sidebarOpenMobile, darkMode } = useAppSelector((state) => state.settings)
  
  // Close mobile sidebar on route change
  useEffect(() => {
    dispatch(closeMobileSidebar())
  }, [location.pathname, dispatch])
  
  const navItems = [
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
    },
    {
      path: '/users',
      icon: Users,
      label: 'Users',
    },
    {
      path: '/organizations',
      icon: Building2,
      label: 'Organizations',
    },
    {
      path: '/communications',
      icon: Mail,
      label: 'Communications',
    },
    {
      path: '/monitoring',
      icon: Activity,
      label: 'Monitoring',
    },
    {
      path: '/admins',
      icon: UserCog,
      label: 'Admins',
    },
    {
      path: '/settings',
      icon: Settings,
      label: 'Settings',
    },
    {
      path: '/print-server/clear-queue',
      icon: Printer,
      label: 'Print Server',
      separator: true,
      children: [
        {
          path: '/print-server/clear-queue',
          icon: Trash2,
          label: 'Clear Printing Queue',
        },
      ],
    },
  ]
  
  return (
    <div className={`
      fixed left-0 top-0 h-full transition-all duration-300 z-50
      bg-white dark:bg-gray-900
      border-r border-border
      ${sidebarCollapsed ? 'w-16' : 'w-64'}
      transform md:transform-none
      ${sidebarOpenMobile ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      flex flex-col
    `}>
      {/* Logo and Controls */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border flex-shrink-0">
        <div className="flex items-center space-x-2">
          <img
            src={darkMode ? SalextLogoWhite : SalextLogo}
            alt="SALEXT"
            className="h-7 w-auto"
          />
          {!sidebarCollapsed && (
            <span className="text-sm font-medium text-foreground">Backoffice</span>
          )}
        </div>

        <div className="flex items-center">
          {/* Mobile Close Button */}
          <button
            onClick={() => dispatch(closeMobileSidebar())}
            className="md:hidden p-2 hover:text-foreground text-muted-foreground"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Desktop Collapse Button */}
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="hidden md:block p-2 hover:text-foreground text-muted-foreground"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="py-4 flex-1 overflow-y-auto">
        {navItems.map((item, index) => (
          <div key={item.path}>
            {/* Add separator if specified */}
            {item.separator && (
              <div className="my-3 mx-4 border-t border-border/50" />
            )}

            {item.children ? (
              <>
                {/* Section header */}
                <div className={`px-4 py-2 text-muted-foreground ${
                  sidebarCollapsed ? 'text-center' : ''
                }`}>
                  {!sidebarCollapsed && (
                    <span className="text-xs font-medium uppercase tracking-wider opacity-60">
                      {item.label}
                    </span>
                  )}
                </div>
                {/* Child items */}
                {!sidebarCollapsed && (
                  <div className="mb-2">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) => `
                          flex items-center space-x-2 px-4 py-2 text-sm transition-colors relative
                          ${isActive
                            ? 'text-primary font-medium bg-primary/10 border-l-4 border-primary'
                            : 'text-muted-foreground hover:text-foreground border-l-4 border-transparent'
                          }
                        `}
                      >
                        <div className="flex items-center space-x-2">
                          {child.icon && <child.icon className="w-4 h-4" />}
                          <span>{child.label}</span>
                        </div>
                      </NavLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <NavLink
                to={item.path}
                className={({ isActive }) => `
                  flex items-center space-x-3 px-4 py-2 transition-colors text-sm relative
                  ${isActive
                    ? 'text-primary font-medium bg-primary/10 border-l-4 border-primary'
                    : 'text-muted-foreground hover:text-foreground border-l-4 border-transparent'
                  }
                  ${sidebarCollapsed ? 'justify-center border-l-0' : ''}
                `}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </NavLink>
            )}
          </div>
        ))}
      </nav>

      {/* Bottom Section */}
      {!sidebarCollapsed && (
        <div className="p-4 border-t border-border flex-shrink-0">
          <div className="px-3 py-2 text-xs text-muted-foreground">
            Version {CONFIG.version}
          </div>
        </div>
      )}
    </div>
  )
}

export default Sidebar